import React from "react";
import { RecurrenceDay } from "../../deals/api/useDeals";
import { FieldLabel, inputClass } from "./FieldLabel";
import { ALL_DAYS, defaultRecurrenceEndDate, weekdayOf } from "./recurrence";

/** "2026-12-31" → "31 Dec 2026". Parsed as parts, not `new Date(str)`, which
 *  treats a bare date as UTC and can render the previous day west of Greenwich. */
function formatEndDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export interface RecurrenceValue {
  recurrenceEnabled: boolean;
  recurrenceDays: RecurrenceDay[];
  recurrenceHasEndDate: boolean;
  recurrenceEndDate: string;
  /** Venue timezone — only used to resolve the fallback year when no start date is set. */
  timezone?: string;
  startTime: string;
}

export function RecurrenceSection({
  value,
  set,
}: {
  value: RecurrenceValue;
  set: <K extends keyof RecurrenceValue>(field: K, v: RecurrenceValue[K]) => void;
}) {
  const toggleDay = (day: RecurrenceDay) => {
    if (value.recurrenceDays.includes(day)) {
      set("recurrenceDays", value.recurrenceDays.filter(d => d !== day));
    } else {
      set("recurrenceDays", [...value.recurrenceDays, day]);
    }
  };

  const toggleEnabled = () => {
    const next = !value.recurrenceEnabled;
    set("recurrenceEnabled", next);
    if (next && value.recurrenceDays.length === 0) {
      const wd = weekdayOf(value.startTime);
      if (wd) set("recurrenceDays", [wd]);
    }
  };

  return (
    <div className="border-t border-gray-800 pt-4 space-y-3">
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          aria-label="Repeat weekly"
          checked={value.recurrenceEnabled}
          onChange={toggleEnabled}
          className="h-4 w-4 accent-violet-500 cursor-pointer"
        />
        <span className="text-sm font-medium text-white">Repeat weekly</span>
      </label>

      {value.recurrenceEnabled && (
        <div className="space-y-3 pl-7">
          <div>
            <FieldLabel required>Days</FieldLabel>
            <div className="flex flex-wrap gap-2">
              {ALL_DAYS.map(d => {
                const on = value.recurrenceDays.includes(d.value);
                return (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => toggleDay(d.value)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                      on
                        ? "bg-violet-600 border-violet-500 text-white"
                        : "bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-600"
                    }`}
                  >
                    {d.short}
                  </button>
                );
              })}
            </div>
            {value.recurrenceDays.length === 0 && (
              <p className="text-xs text-amber-400/80 mt-1.5">Select at least one day.</p>
            )}
          </div>

          <div>
            <FieldLabel>Until</FieldLabel>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  aria-label="No end date"
                  name="recurrence-end"
                  checked={!value.recurrenceHasEndDate}
                  onChange={() => {
                    set("recurrenceHasEndDate", false);
                    set("recurrenceEndDate", "");
                  }}
                  className="h-4 w-4 accent-violet-500"
                />
                <span className="text-sm text-gray-300">
                  No end date —{' '}
                  <span className="text-gray-400">
                    ends {formatEndDate(defaultRecurrenceEndDate(value.startTime, value.timezone ?? "UTC"))}
                  </span>
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  aria-label="End date"
                  name="recurrence-end"
                  checked={value.recurrenceHasEndDate}
                  onChange={() => set("recurrenceHasEndDate", true)}
                  className="h-4 w-4 accent-violet-500"
                />
                <input
                  type="date"
                  value={value.recurrenceEndDate}
                  onChange={e => {
                    set("recurrenceEndDate", e.target.value);
                    if (e.target.value) set("recurrenceHasEndDate", true);
                  }}
                  disabled={!value.recurrenceHasEndDate}
                  className={`${inputClass} max-w-[200px] disabled:opacity-50`}
                />
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
